/**
 * ScoreThresholds — word score brackets that trigger in-turn bonuses.
 *
 * Higher-scoring words unlock effects like bonus damage
 * and damage multipliers. Only the highest matched threshold applies.
 *
 * Thresholds scale naturally with grid progression:
 *   3x3 → barely hits Solid
 *   4x4 → Solid often, Strong sometimes
 *   5x5 → all tiers achievable by skilled players
 */

export interface ScoreThreshold {
  /** Minimum word score to trigger this tier. */
  minScore: number;
  /** Display name shown as floating text. */
  name: string;
  /** Color for the floating text. */
  color: string;
  /** Flat bonus damage added to the word's damage. */
  damageBonus: number;
  /** Multiplier applied to total damage (after flat bonus). */
  damageMultiplier: number;
  /** Flat HP heal applied to the player. */
  healAmount: number;
}

/**
 * Thresholds ordered highest-first. matchThreshold() returns the first match.
 */
const THRESHOLDS: ScoreThreshold[] = [
  {
    minScore: 25,
    name: "LEGENDARY",
    color: "#fbbf24",
    damageBonus: 0,
    damageMultiplier: 2,

    healAmount: 2,
  },
  {
    minScore: 18,
    name: "MIGHTY",
    color: "#f97316",
    damageBonus: 0,
    damageMultiplier: 1.5,

    healAmount: 0,
  },
  {
    minScore: 12,
    name: "STRONG",
    color: "#22d3ee",
    damageBonus: 0,
    damageMultiplier: 1,

    healAmount: 0,
  },
  {
    minScore: 8,
    name: "SOLID",
    color: "#a3e635",
    damageBonus: 1,
    damageMultiplier: 1,

    healAmount: 0,
  },
];

/**
 * Find the highest threshold the word score qualifies for.
 * Returns null if score is below all thresholds.
 */
export function matchThreshold(wordScore: number): ScoreThreshold | null {
  for (const t of THRESHOLDS) {
    if (wordScore >= t.minScore) return t;
  }
  return null;
}

/** Get all thresholds (for UI display of brackets). */
export function getAllThresholds(): readonly ScoreThreshold[] {
  return THRESHOLDS;
}
